import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Ranking as RankingIcon,
  Award,
  Medal,
  Cup,
  InfoCircle,
} from "iconsax-react";
import { Spin } from "antd";
import { useAuth } from "../../hooks/auth";
import {
  weeklyLeaguesService,
  LEAGUE_NAMES,
  LEAGUE_DESCRIPTIONS,
  XP_VALUES,
  type CohortRanking,
  type WeeklyLeagueEntry,
} from "../../services/weeklyLeaguesService";
import MobileLayout from "../../components/MobileLayout";
import {
  LeagueInfoCard,
  PodiumContainer,
  PodiumItem,
  RankingList,
  RankingItem,
  WeekInfoBar,
  XpLegendCard,
  EmptyState,
} from "./styles";

function formatWeekRange(weekStart: number, weekEnd: number): string {
  const start = new Date(weekStart);
  const end = new Date(weekEnd);
  const opts: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
  };
  return `${start.toLocaleDateString("pt-BR", opts)} – ${end.toLocaleDateString(
    "pt-BR",
    { ...opts, year: "numeric" }
  )}`;
}

const Ranking: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ranking, setRanking] = useState<CohortRanking | null>(null);
  const [leagueInfo, setLeagueInfo] = useState<WeeklyLeagueEntry | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [rankingData, infoData] = await Promise.all([
        weeklyLeaguesService.getCohortRanking(user.uid),
        weeklyLeaguesService.getUserLeagueInfo(user.uid),
      ]);

      setRanking(rankingData);
      setLeagueInfo(infoData);
    } catch (error) {
      console.error("Erro ao carregar ranking:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.uid) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user, loadData]);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const goToProfile = (entry: WeeklyLeagueEntry) => {
    navigate(`/ranking/user/${entry.userId}`, {
      state: {
        initialDisplayName: entry.displayName,
        initialPhotoURL: entry.photoURL,
      },
    });
  };

  const topThree = ranking?.entries.slice(0, 3) || [];
  const others = ranking?.entries.slice(3) || [];
  const hasEntries = (ranking?.entries.length || 0) > 0;

  const leagueName = leagueInfo
    ? LEAGUE_NAMES[leagueInfo.leagueLevel] || `Liga ${leagueInfo.leagueLevel}`
    : null;
  const leagueDescription = leagueInfo
    ? LEAGUE_DESCRIPTIONS[leagueInfo.leagueLevel]
    : null;

  const weekLabel =
    leagueInfo?.weekStart && leagueInfo?.weekEnd
      ? formatWeekRange(leagueInfo.weekStart, leagueInfo.weekEnd)
      : null;

  return (
    <MobileLayout headerTitle="Ranking Semanal" headerType="private">
      <div style={{ padding: "0 16px", marginTop: "16px", paddingBottom: 32 }}>
        {loading ? (
          <EmptyState>
            <Spin size="large" />
            <p>Carregando ranking…</p>
          </EmptyState>
        ) : (
          <>
            {leagueInfo && (
              <>
                <LeagueInfoCard>
                  <div className="league-icon">
                    <RankingIcon size="32" variant="Bold" color="white" />
                  </div>
                  <div className="info">
                    <h3>{leagueName}</h3>
                    <p>
                      {leagueDescription
                        ? `${leagueDescription} · Sua divisão atual`
                        : "Sua divisão atual na semana"}
                    </p>
                  </div>
                  <div className="stats">
                    <div className="position">
                      #{leagueInfo.position || "-"}
                    </div>
                    <div className="total">na liga</div>
                  </div>
                </LeagueInfoCard>

                {weekLabel && (
                  <WeekInfoBar>
                    <span className="label">Semana</span>
                    <span className="value">{weekLabel}</span>
                    <span className="xp">
                      {leagueInfo.xp} XP · {ranking?.entries.length || 0}{" "}
                      participantes
                    </span>
                  </WeekInfoBar>
                )}
              </>
            )}

            {!leagueInfo && (
              <EmptyState>
                <div className="icon-wrap">
                  <Cup size={48} variant="Bold" color="var(--color-primary)" />
                </div>
                <h3>Participe de jogos para entrar na liga!</h3>
                <p>
                  Complete jogos educativos e consulte sinais para ganhar XP e
                  competir na liga semanal.
                </p>
              </EmptyState>
            )}

            {leagueInfo && hasEntries && (
              <>
                <PodiumContainer>
                  {topThree[1] && (
                    <PodiumItem
                      position={2}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToProfile(topThree[1])}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goToProfile(topThree[1]);
                        }
                      }}
                    >
                      <div className="avatar-wrapper">
                        {topThree[1].photoURL ? (
                          <img
                            src={topThree[1].photoURL}
                            alt={topThree[1].displayName}
                          />
                        ) : (
                          <div className="initials">
                            {getInitials(topThree[1].displayName)}
                          </div>
                        )}
                      </div>
                      <div className="name">
                        {topThree[1].displayName}
                        {topThree[1].userId === user?.uid ? " (Você)" : ""}
                      </div>
                      <div className="xp">{topThree[1].xp} XP</div>
                      <Medal className="rank-icon" size="32" variant="Bold" />
                    </PodiumItem>
                  )}

                  {topThree[0] && (
                    <PodiumItem
                      position={1}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToProfile(topThree[0])}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goToProfile(topThree[0]);
                        }
                      }}
                    >
                      <div className="avatar-wrapper">
                        {topThree[0].photoURL ? (
                          <img
                            src={topThree[0].photoURL}
                            alt={topThree[0].displayName}
                          />
                        ) : (
                          <div className="initials">
                            {getInitials(topThree[0].displayName)}
                          </div>
                        )}
                      </div>
                      <div className="name">
                        {topThree[0].displayName}
                        {topThree[0].userId === user?.uid ? " (Você)" : ""}
                      </div>
                      <div className="xp">{topThree[0].xp} XP</div>
                      <Award className="rank-icon" size="48" variant="Bold" />
                    </PodiumItem>
                  )}

                  {topThree[2] && (
                    <PodiumItem
                      position={3}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToProfile(topThree[2])}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goToProfile(topThree[2]);
                        }
                      }}
                    >
                      <div className="avatar-wrapper">
                        {topThree[2].photoURL ? (
                          <img
                            src={topThree[2].photoURL}
                            alt={topThree[2].displayName}
                          />
                        ) : (
                          <div className="initials">
                            {getInitials(topThree[2].displayName)}
                          </div>
                        )}
                      </div>
                      <div className="name">
                        {topThree[2].displayName}
                        {topThree[2].userId === user?.uid ? " (Você)" : ""}
                      </div>
                      <div className="xp">{topThree[2].xp} XP</div>
                      <Cup className="rank-icon" size="28" variant="Bold" />
                    </PodiumItem>
                  )}
                </PodiumContainer>

                <RankingList>
                  {others.map((entry) => (
                    <RankingItem
                      key={entry.userId}
                      isUser={entry.userId === user?.uid}
                      role="button"
                      tabIndex={0}
                      onClick={() => goToProfile(entry)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          goToProfile(entry);
                        }
                      }}
                    >
                      <div className="index">{entry.position}</div>
                      <div className="avatar">
                        {entry.photoURL ? (
                          <img src={entry.photoURL} alt={entry.displayName} />
                        ) : (
                          <div className="initials">
                            {getInitials(entry.displayName)}
                          </div>
                        )}
                      </div>
                      <div className="user-info">
                        <div className="name">
                          {entry.displayName}
                          {entry.userId === user?.uid ? " (Você)" : ""}
                        </div>
                        <div className="league-tag">Competidor da Semana</div>
                      </div>
                      <div className="xp-stats">
                        <div className="xp-value">{entry.xp}</div>
                        <div className="xp-label">XP</div>
                      </div>
                    </RankingItem>
                  ))}
                </RankingList>
              </>
            )}

            {leagueInfo && !hasEntries && (
              <EmptyState>
                <div className="icon-wrap">
                  <Medal
                    size={40}
                    variant="Bold"
                    color="var(--color-primary)"
                  />
                </div>
                <h3>Ranking ainda vazio</h3>
                <p>
                  Você já está na liga. Continue ganhando XP para aparecer no
                  pódio.
                </p>
              </EmptyState>
            )}

            <XpLegendCard>
              <div className="legend-header">
                <InfoCircle size={22} variant="Bold" />
                <h4>O que vale (XP por ação)</h4>
              </div>
              <ul>
                <li>
                  <span>Consulta a sinal</span>
                  <strong>{XP_VALUES.CONSULTA_SIMPLES} XP</strong>
                </li>
                <li>
                  <span>Acerto no jogo da memória</span>
                  <strong>{XP_VALUES.JOGO_MEMORIA_ACERTO} XP</strong>
                </li>
                <li>
                  <span>Quiz de datilologia</span>
                  <strong>{XP_VALUES.QUIZ_DATILOLOGIA} XP</strong>
                </li>
                <li>
                  <span>Revisão diária</span>
                  <strong>{XP_VALUES.REVISAO_DIARIA} XP</strong>
                </li>
                <li>
                  <span>Gravação de vídeo</span>
                  <strong>{XP_VALUES.GRAVACAO_VIDEO} XP</strong>
                </li>
              </ul>
            </XpLegendCard>
          </>
        )}
      </div>
    </MobileLayout>
  );
};

export default Ranking;
